import { useQuery } from "@apollo/client";
import { Box, Button } from "@mui/material";
import { groupBy } from "lodash";
import { ReactElement, useCallback, useMemo, useState } from "react";
import {
  ArrayParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  EditSubaccountDefaultMessagingStrategyBandwidthShortCodeMutation,
  EditSubaccountDefaultMessagingStrategyBandwidthShortCodeMutationVariables,
  EditSubaccountDefaultMessagingStrategyBandwidthTollFreeMutation,
  EditSubaccountDefaultMessagingStrategyBandwidthTollFreeMutationVariables,
  EditSubaccountDefaultMessagingStrategyFakeTollFreeMutation,
  EditSubaccountDefaultMessagingStrategyFakeTollFreeMutationVariables,
  EditSubaccountDefaultMessagingStrategyFakeShortCodeMutation,
  EditSubaccountDefaultMessagingStrategyFakeShortCodeMutationVariables,
  EditSubaccountDefaultMessagingStrategyTwilioShortCodeMutation,
  EditSubaccountDefaultMessagingStrategyTwilioShortCodeMutationVariables,
  ListOwnedPhoneNumberPoolsQuery,
  ListOwnedPhoneNumbersQuery,
  ListOwnedPhoneNumbersQueryVariables,
  PhoneNumberMessageClass,
  ProviderName,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { CollapsibleContainer } from "~frontend/components/CollapsibleContainer";
import { FlexBox } from "~frontend/components/FlexBox";
import PageContainer from "~frontend/components/PageContainer";
import { PageHeader } from "~frontend/components/PageHeader";
import { LIST_OWNED_PHONE_NUMBERS } from "~frontend/graphql/ownedPhoneNumber";
import { LIST_OWNED_PHONE_NUMBER_POOLS } from "~frontend/graphql/ownedPhoneNumberPool";
import {
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_SHORT_CODE,
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_TOLL_FREE,
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_SHORT_CODE,
  EDIT_SUBACCOUNT_DEFAULT_MESSAGING_TWILIO_SHORT_CODE,
  GET_SUBACCOUNT_ADMIN_FIELDS,
} from "~frontend/graphql/subaccounts";
import { LIST_10DLC_CAMPAIGNS } from "~frontend/graphql/tenDlcCampaigns";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useNonCacheClearingApolloClient } from "~frontend/providers/NonCacheClearingApolloClientProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { PhoneNumberSortBy } from "~frontend/routes/subaccounts/phoneNumberList/constants";
import useMutation from "~frontend/util/useMutation";

import { ImportOwnedPhoneNumberDialog } from "./ImportOwnedPhoneNumberDialog";
import { InProgressTollFreeRegistrations } from "./InProgressTollFreeRegistrations";
import PhoneNumberListControls from "./PhoneNumberListControls";
import PhoneNumberPools from "./PhoneNumberPools";
import { PhoneNumberTable } from "./PhoneNumberTable";
import { TenDlc } from "./TenDlc";
import { OwnedPhoneNumberForList } from "./types";

export const PHONE_NUMBER_REFETCH_QUERIES = [
  LIST_OWNED_PHONE_NUMBERS,
  LIST_10DLC_CAMPAIGNS,
  GET_SUBACCOUNT_ADMIN_FIELDS,
];

export function PhoneNumbersList(): ReactElement {
  const { subaccount, subaccountLink } = useSubaccount()!;
  const subaccountApolloClient = useNonCacheClearingApolloClient();

  useBreadcrumbs([
    {
      label: "Phone Numbers",
      path: subaccountLink("/phone-numbers"),
    },
  ]);

  const [providerFilter, setProviderFilter] = useQueryParam(
    "provider",
    withDefault(ArrayParam, Object.values(ProviderName))
  );

  const [messageClassFilter, setMessageClassFilter] = useQueryParam(
    "messageClass",
    withDefault(
      ArrayParam,
      Object.values(PhoneNumberMessageClass).filter(
        m => m !== PhoneNumberMessageClass.Unregistered
      )
    )
  );

  const [sort, setSort] = useQueryParam(
    "sort",
    withDefault(StringParam, PhoneNumberSortBy.CreatedAtDesc)
  );

  const listOwnedPhoneNumberPoolsResult =
    useQuery<ListOwnedPhoneNumberPoolsQuery>(LIST_OWNED_PHONE_NUMBER_POOLS, {
      variables: {
        input: {
          subaccountId: subaccount.id,
        },
      },
    });

  const ownedPhoneNumberPools = useMemo(() => {
    return (
      listOwnedPhoneNumberPoolsResult.data?.listOwnedPhoneNumberPools || []
    );
  }, [listOwnedPhoneNumberPoolsResult.data?.listOwnedPhoneNumberPools]);

  const { data, error, loading } = useQuery<
    ListOwnedPhoneNumbersQuery,
    ListOwnedPhoneNumbersQueryVariables
  >(LIST_OWNED_PHONE_NUMBERS, {
    variables: {
      input: {
        subaccountId: subaccount.id,
      },
    },
  });

  const [dialogOpen, setDialogOpen] = useState(false);

  const showSection = (messageClass: PhoneNumberMessageClass) =>
    messageClassFilter.length === 0 ||
    (messageClassFilter as PhoneNumberMessageClass[]).includes(messageClass);

  const [setDefaultMessagingStrategyTwilioShortCode] = useMutation<
    EditSubaccountDefaultMessagingStrategyTwilioShortCodeMutation,
    EditSubaccountDefaultMessagingStrategyTwilioShortCodeMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_TWILIO_SHORT_CODE, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const [setDefaultMessagingStrategyBandwidthShortCode] = useMutation<
    EditSubaccountDefaultMessagingStrategyBandwidthShortCodeMutation,
    EditSubaccountDefaultMessagingStrategyBandwidthShortCodeMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_SHORT_CODE, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const [setDefaultMessagingStrategyFakeShortCode] = useMutation<
    EditSubaccountDefaultMessagingStrategyFakeShortCodeMutation,
    EditSubaccountDefaultMessagingStrategyFakeShortCodeMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_FAKE_SHORT_CODE, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const [setDefaultMessagingStrategyBandwidthTollFree] = useMutation<
    EditSubaccountDefaultMessagingStrategyBandwidthTollFreeMutation,
    EditSubaccountDefaultMessagingStrategyBandwidthTollFreeMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_TOLL_FREE, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const [setDefaultMessagingStrategyFakeTollFree] = useMutation<
    EditSubaccountDefaultMessagingStrategyFakeTollFreeMutation,
    EditSubaccountDefaultMessagingStrategyFakeTollFreeMutationVariables
  >(EDIT_SUBACCOUNT_DEFAULT_MESSAGING_STRATEGY_BANDWIDTH_TOLL_FREE, {
    refetchQueries: [GET_SUBACCOUNT_ADMIN_FIELDS],
    awaitRefetchQueries: true,
    client: subaccountApolloClient,
  });

  const setDefaultPhoneNumber = useCallback(
    async (phoneNumber: OwnedPhoneNumberForList) => {
      if (
        phoneNumber.provider === ProviderName.Twilio &&
        phoneNumber.messageClass === PhoneNumberMessageClass.ShortCode
      ) {
        await setDefaultMessagingStrategyTwilioShortCode({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else if (
        phoneNumber.provider === ProviderName.Bandwidth &&
        phoneNumber.messageClass === PhoneNumberMessageClass.ShortCode
      ) {
        await setDefaultMessagingStrategyBandwidthShortCode({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else if (
        phoneNumber.provider === ProviderName.Fake &&
        phoneNumber.messageClass === PhoneNumberMessageClass.ShortCode
      ) {
        await setDefaultMessagingStrategyFakeShortCode({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else if (
        phoneNumber.provider === ProviderName.Bandwidth &&
        phoneNumber.messageClass === PhoneNumberMessageClass.TollFree
      ) {
        await setDefaultMessagingStrategyBandwidthTollFree({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else if (
        phoneNumber.provider === ProviderName.Fake &&
        phoneNumber.messageClass === PhoneNumberMessageClass.TollFree
      ) {
        await setDefaultMessagingStrategyFakeTollFree({
          variables: {
            input: {
              subaccountId: subaccount.id,
              config: { ownedPhoneNumberId: phoneNumber.id },
            },
          },
        });
      } else {
        throw new Error(
          `Unrecognized provider and message class combination: ${phoneNumber.provider}, ${phoneNumber.messageClass}`
        );
      }
    },
    [
      subaccount,
      setDefaultMessagingStrategyTwilioShortCode,
      setDefaultMessagingStrategyBandwidthShortCode,
      setDefaultMessagingStrategyBandwidthTollFree,
      setDefaultMessagingStrategyFakeShortCode,
      setDefaultMessagingStrategyFakeTollFree,
    ]
  );

  return (
    <PageContainer>
      <PageHeader>Phone Numbers</PageHeader>
      <PhoneNumberListControls
        onClickImportNumber={async () => setDialogOpen(true)}
        providerFilter={providerFilter as string[]}
        setProviderFilter={setProviderFilter}
        messageClassFilter={messageClassFilter as string[]}
        setMessageClassFilter={setMessageClassFilter}
        setSort={setSort}
        sort={sort}
      />
      <FlexBox direction="column" sx={{ mt: 3 }} gap={3}>
        <AsyncWrapper
          data={data?.listOwnedPhoneNumbers}
          loading={loading}
          errors={error}
        >
          {ownedPhoneNumbers => {
            const numbersByMessageClass = groupBy(
              ownedPhoneNumbers,
              "messageClass"
            );
            const activeTFNumberExists = numbersByMessageClass.TOLL_FREE?.some(
              m => m.state === "ACTIVE"
            );

            return (
              <>
                {showSection(PhoneNumberMessageClass.TenDlc) && (
                  <TenDlc
                    numbers={numbersByMessageClass.TEN_DLC || []}
                    sort={sort}
                    providerFilter={providerFilter as string[]}
                  />
                )}

                {showSection(PhoneNumberMessageClass.TollFree) && (
                  <CollapsibleContainer
                    header="Toll-Free Numbers"
                    headerActions={
                      <Button
                        variant="contained"
                        color="primary"
                        sx={{ fontSize: 12 }}
                        href={subaccountLink(
                          `/phone-numbers/toll-free-registration`
                        )}
                      >
                        Register for Toll-Free
                      </Button>
                    }
                  >
                    <InProgressTollFreeRegistrations />

                    <Box ml={3}>
                      <PhoneNumberTable
                        rows={numbersByMessageClass.TOLL_FREE || []}
                        sort={sort}
                        providerFilter={providerFilter as string[]}
                        setDefaultPhoneNumber={setDefaultPhoneNumber}
                      />
                    </Box>
                  </CollapsibleContainer>
                )}
                {showSection(PhoneNumberMessageClass.ShortCode) && (
                  <CollapsibleContainer header="Short Code Numbers">
                    <Box ml={3}>
                      <PhoneNumberTable
                        rows={numbersByMessageClass.SHORT_CODE || []}
                        sort={sort}
                        providerFilter={providerFilter as string[]}
                        setDefaultPhoneNumber={setDefaultPhoneNumber}
                      />
                    </Box>
                  </CollapsibleContainer>
                )}
                <PhoneNumberPools
                  subaccountId={subaccount.id}
                  ownedPhoneNumberPools={ownedPhoneNumberPools}
                />
                {dialogOpen && (
                  <ImportOwnedPhoneNumberDialog
                    onClose={() => setDialogOpen(false)}
                    hasActiveTFNumber={activeTFNumberExists}
                  />
                )}
              </>
            );
          }}
        </AsyncWrapper>
      </FlexBox>
    </PageContainer>
  );
}
